import React, { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';

import { functions } from '../firebase';
import { AccessDenied, canAccess } from '../adminAccess';
import DatabaseError from '../components/DatabaseError';
import Table from '../components/Table';
import { AccountBadge, DateTime, PageHeader } from '../components/AdminUI';

export default function NewUsersPage({ user }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await httpsCallable(functions, 'listNewUsers')();
      const rows = result.data?.users || [];
      setUsers(rows);
      setRoles(current => Object.fromEntries(rows.map(account => [
        account.uid,
        current[account.uid] || account.role || '',
      ])));
    } catch (err) {
      setError(err?.message || 'Unable to load new users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function act(account, action) {
    const role = roles[account.uid] || '';
    if (action === 'approve' && !['client', 'worker'].includes(role)) {
      setError('Please assign this user as Client or Worker before approval.');
      return;
    }
    if (action === 'reject' && !window.confirm('Reject this account request?')) return;
    try {
      setWorking(account.uid);
      setError('');
      await httpsCallable(functions, 'manageUserAccess')({
        uid: account.uid,
        action,
        ...(action === 'approve' ? { role } : {}),
      });
      setNotice(action === 'approve' ? 'User approved.' : 'User rejected.');
      await load();
      window.dispatchEvent(new Event('pakmazar:pending-users-changed'));
    } catch (err) {
      setError(err?.message || 'Unable to update this account.');
    } finally {
      setWorking('');
    }
  }

  if (!canAccess(user, 'users')) return <AccessDenied />;

  return (
    <div className="page-shell">
      <PageHeader title="New Users" description="Review first-time registration requests and assign access roles.">
        <button className="btn-secondary" onClick={load} disabled={loading}>Refresh</button>
      </PageHeader>
      {notice && <p className="mb-3 text-sm text-green-700">{notice}</p>}
      <DatabaseError message={error} onRetry={load} />
      {loading ? <p className="py-10 text-center text-gray-500">Loading...</p> : (
        <Table
          headers={['User', 'Email', 'Method', 'Date', 'Role', 'Verification', 'Status', 'Actions']}
          empty="No pending users"
        >
          {users.map(account => (
            <tr key={account.uid} className="border-b border-gray-50 text-sm">
              <td className="px-4 py-3 font-medium">{account.name || '—'}</td>
              <td className="px-4 py-3">{account.email || '—'}</td>
              <td className="px-4 py-3">{account.signupMethod}</td>
              <td className="px-4 py-3"><DateTime value={account.registrationDate} /></td>
              <td className="px-4 py-3">
                <select
                  value={roles[account.uid] || ''}
                  onChange={event => setRoles(current => ({ ...current, [account.uid]: event.target.value }))}
                  className="input-field min-w-[132px] py-1.5"
                >
                  <option value="">Assign role</option>
                  <option value="client">Client</option>
                  <option value="worker">Worker</option>
                </select>
              </td>
              <td className="px-4 py-3"><AccountBadge kind={account.emailVerified ? 'verified' : 'unverified'}>{account.emailVerified ? 'Verified' : 'Unverified'}</AccountBadge></td>
              <td className="px-4 py-3"><AccountBadge kind="pending">Pending</AccountBadge></td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-2">
                  <button className="btn-secondary" onClick={() => window.alert(JSON.stringify(account, null, 2))}>View</button>
                  <button className="btn-primary" disabled={working === account.uid || !['client', 'worker'].includes(roles[account.uid])} onClick={() => act(account, 'approve')}>Approve</button>
                  <button className="btn-danger" disabled={working === account.uid} onClick={() => act(account, 'reject')}>Reject</button>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}
